import { Box, Typography, useTheme, alpha } from "@mui/material";
import React from "react";

interface AddNewCardProps {
  compact?: boolean;
  onAdd: () => void;
  entityName?: string;
}

// Minimal Plus SVG Component - just the + symbol
const PlusIcon: React.FC<{ size: number; color: string }> = ({ size, color }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 100 100" 
    xmlns="http://www.w3.org/2000/svg"
  >
    <path 
      d="M50 15 L50 85 M15 50 L85 50" 
      stroke={color} 
      strokeWidth="8" 
      strokeLinecap="round" 
    />
  </svg>
);

const AddNewCard: React.FC<AddNewCardProps> = ({ 
  compact = false, 
  onAdd, 
  entityName = "item" 
}) => {
  const theme = useTheme();
  
  // Determine the best contrast color based on theme
  const iconColor = theme.palette.mode === 'dark' 
    ? theme.palette.common.white 
    : theme.palette.common.black;
    
  const highlightColor = theme.palette.primary.main;

  return (
    <Box
      sx={{
        width: "100%",
        height: compact ? 120 : 400,
        aspectRatio: compact ? undefined : "1/1",
        cursor: "pointer",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        transition: "all 0.2s ease-in-out",
        backgroundColor: "transparent",
        "&:hover": {
          "& .plus-icon": {
            borderColor: highlightColor,
            borderWidth: 2,
          },
        },
      }}
      onClick={onAdd}
    >
      <Box
        className="plus-icon"
        sx={{
          mb: compact ? 0.5 : 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: iconColor,
          borderRadius: "50%",
          border: "2px solid transparent",
          padding: compact ? 1 : 2,
          transition: "border-color 0.2s ease-in-out",
          filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.1))",
        }}
      >
        <PlusIcon
          size={compact ? 48 : 96}
          color="currentColor"
        />
      </Box>
      {!compact && (
        <Typography
          variant="body2"
          color={iconColor}
          textAlign="center"
          sx={{ 
            fontWeight: 600,
            opacity: 0.8,
            textShadow: "0 1px 2px rgba(0,0,0,0.1)",
          }}
        >
          Add New {entityName}
        </Typography>
      )}
    </Box>
  );
};

export default AddNewCard; 